/**
 * Domain enumok — a spec 14. §-ának megfelelően.
 *
 * Ezek az értékek megjelennek a Prisma sémában, az OpenAPI dokumentumban és
 * (generálva) az Android kliensben is. Egy érték átnevezése adatmigrációt
 * jelent, mert a DB-ben és az eseménynaplóban is ez a string szerepel.
 */
/**
 * A HÁROM lokációtípus (G fázis, 2026-08-14). Korábban hat volt.
 *
 * A `STAGING`, `WORKSTATION` és `OTHER` megszűnt: mindhárom pontosan azt
 * csinálta, amit a `STORAGE`, csak más néven — cserébe minden felületi és
 * szolgáltatásbeli elágazásnak hat ágat kellett kezelnie. Az Előkészítő
 * ezért ma `STORAGE` típusú; hogy MELYIK tároló az alapértelmezett, azt az
 * `is_default_staging` flag mondja meg, nem a típus.
 *
 * MINDHÁROM VÉGSŐ LOKÁCIÓ: egyik sem tárolható a másikba. A D fázisban a
 * raklap még betárolható volt egy polcszintre; ezt a megrendelő elvetette,
 * mert a mappa fizikai helye így két lépésből állt, és minden kérdésre
 * kétféle úton lehetett válaszolni.
 *
 * SZINTES TÍPUSOK: `PALLET` és `SHELF`. A kettő szint-viselkedése ELTÉR, és
 * ezt egy helyen, a `hasLevels`/`hasDynamicLevels` dönti el.
 */
export const LocationType = {
    /** Raklap (`RKL-####`). Szintjei DINAMIKUSAK (ld. lent). */
    PALLET: 'PALLET',
    /**
     * Polc (`POL-####`) — fizikailag fix szintekkel.
     *
     * A raklappal ellentétben itt teljesen normális, hogy egy szint üres: a
     * polc szintjeit a szerkezete adja, nem az, hogy mi van rajta. A
     * szintek létrehozáskor egyszerre jönnek létre, és üresen is megmaradnak.
     *
     * Csak a polcszint LEZÁRHATÓ (`locked_at`), és csak a webről.
     */
    SHELF: 'SHELF',
    /** Tároló (`TAR-####`) — szint nélküli hely. Ilyen az Előkészítő is. */
    STORAGE: 'STORAGE',
};
/**
 * Van-e a lokációtípusnak szintje.
 *
 * EGYETLEN forrás: a `folder_location_assignment` CHECK constraintje
 * ugyanezt a halmazt kényszeríti ki DB-szinten („szint kötelező PALLET és
 * SHELF esetén, tilos minden másnál"). Ha ez a függvény és a constraint
 * eltérne, a szolgáltatásréteg olyan műveletet engedne át, amit az
 * adatbázis visszautasít — ezért a két helyet együtt kell módosítani, és
 * erre teszt is van.
 */
export function hasLevels(type) {
    return type === LocationType.PALLET || type === LocationType.SHELF;
}
/**
 * Dinamikusan jönnek-e létre és szűnnek-e meg a szintek.
 *
 * `PALLET`: igen. A raklap szintjeit elválasztó lemezek hozzák létre, tehát
 * fizikailag nincs értelme üres szintnek — új szint csak akkor születik, ha
 * az alatta lévőn már van mappa, és üresedéskor megszűnik.
 *
 * `SHELF`: nem. A polc szintjei a szerkezetéből adódnak.
 */
export function hasDynamicLevels(type) {
    return type === LocationType.PALLET;
}
export const LocationStatus = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
};
/** Spec 14.1 § — feldolgozási státuszok. */
export const FolderStatus = {
    /** Fotó megvan, felismerés még nem futott le. */
    CAPTURED: 'CAPTURED',
    /** Azure AI feldolgozás folyamatban. */
    OCR_PENDING: 'OCR_PENDING',
    /** Alacsony megbízhatóság — emberi ellenőrzés kell. */
    REVIEW_REQUIRED: 'REVIEW_REQUIRED',
    /** A szöveget ember jóváhagyta. */
    APPROVED: 'APPROVED',
    ARCHIVED: 'ARCHIVED',
};
/** Spec 14.5 §. */
export const PalletStatus = {
    OPEN: 'OPEN',
    CLOSED: 'CLOSED',
    IN_TRANSIT: 'IN_TRANSIT',
    STORED: 'STORED',
    EMPTY: 'EMPTY',
    DISMANTLED: 'DISMANTLED',
};
// A `PalletLevelStatus` (nyitott/zárt/üres) MEGSZŰNT a D fázisban.
//
// A spec 14.6 §-a szintenkénti státuszt írt elő, és a mozgatás státuszhoz
// volt kötve. A valós használat megcáfolta: a dolgozónak bármikor kell
// tudnia mappát betenni és kivenni, és a szint megnyitása/lezárása üres
// adminisztratív lépés volt egy fizikailag nem létező állapotról.
//
// A dinamikus szintekkel (ld. hasDynamicLevels) a fogalom értelmét is
// vesztette: egy szint attól létezik, hogy van rajta mappa.
//
// Az ESEMÉNYTÍPUSOK (PALLET_LEVEL_OPENED/CLOSED/REOPENED) NEM tűnhetnek el,
// mert meglévő eseménysorok hivatkoznak rájuk — ld. az EventType blokkot.
/** Spec 14.3 § — melyik motor adta az eredményt. */
export const RecognitionType = {
    LOCAL_OCR: 'LOCAL_OCR',
    AZURE_AI: 'AZURE_AI',
};
export const RecognitionStatus = {
    QUEUED: 'QUEUED',
    RUNNING: 'RUNNING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
};
/** Melyik kliens indította a műveletet (spec 12. § `source_client`). */
export const SourceClient = {
    ZEBRA: 'ZEBRA',
    WEB: 'WEB',
    SYSTEM: 'SYSTEM',
};
/**
 * Eseménytípusok — a spec 12. §-ából kiindulva, fázisonként bővítve.
 *
 * A G fázisban (2026-08-14) a lista MEGTISZTULT: a `PALLET_LEVEL_OPENED`,
 * `_CLOSED`, `_REOPENED` a D fázis óta nem íródott, a
 * `PALLET_PLACED_ON_SHELF` / `_REMOVED_FROM_SHELF` pedig a lokáció-
 * hierarchiával együtt szűnt meg. Törölni azért lehetett őket, mert az
 * `event_log` ugyanabban a migrációban ürült ki — máskülönben egy meglévő
 * sor hivatkozása miatt csak elavultnak jelölni lehetne őket.
 *
 * FOLDER_IMAGE_ADDED mellett nincs FOLDER_IMAGE_REPLACED — az eredeti kép
 * nem írható felül (12. alapelv), új kép mindig új esemény. A törlés
 * (`FOLDER_IMAGE_DELETED`) ennek szűk, GDPR-indokolt kivétele, és kizárólag
 * NEM elsődleges képre.
 */
export const EventType = {
    FOLDER_CREATED: 'FOLDER_CREATED',
    FOLDER_IMAGE_ADDED: 'FOLDER_IMAGE_ADDED',
    /** G4: melyik kép az elsődleges — az, amit a felületek elöl mutatnak. */
    FOLDER_IMAGE_PRIMARY_CHANGED: 'FOLDER_IMAGE_PRIMARY_CHANGED',
    /** G4: NEM elsődleges kép törlése, kötelező indokkal. */
    FOLDER_IMAGE_DELETED: 'FOLDER_IMAGE_DELETED',
    LOCAL_OCR_COMPLETED: 'LOCAL_OCR_COMPLETED',
    AI_RECOGNITION_REQUESTED: 'AI_RECOGNITION_REQUESTED',
    AI_RECOGNITION_COMPLETED: 'AI_RECOGNITION_COMPLETED',
    FOLDER_TEXT_UPDATED: 'FOLDER_TEXT_UPDATED',
    /**
     * A gerincszövegből SZÁRMAZTATOTT mezők kézi javítása (E4).
     *
     * Külön típus a `FOLDER_TEXT_UPDATED`-től, mert más tényt rögzít: a
     * `current_text` NEM változott, csak a belőle számolt mezők egyike.
     */
    FOLDER_SPINE_FIELDS_UPDATED: 'FOLDER_SPINE_FIELDS_UPDATED',
    /** F1: törzsadat-változás — a 3. alapelv szerint ez is tény. */
    COMPANY_CREATED: 'COMPANY_CREATED',
    COMPANY_UPDATED: 'COMPANY_UPDATED',
    /** G3: a cégillesztés elől kivont minták (pl. a mappacímke felirata). */
    SPINE_IGNORE_PATTERN_CREATED: 'SPINE_IGNORE_PATTERN_CREATED',
    SPINE_IGNORE_PATTERN_UPDATED: 'SPINE_IGNORE_PATTERN_UPDATED',
    /** F2: a gerincadatok tömeges újraelemzése. */
    SPINE_REPARSED: 'SPINE_REPARSED',
    FOLDER_LOCATION_CHANGED: 'FOLDER_LOCATION_CHANGED',
    FOLDER_ASSIGNED_TO_PALLET: 'FOLDER_ASSIGNED_TO_PALLET',
    FOLDER_REMOVED_FROM_PALLET: 'FOLDER_REMOVED_FROM_PALLET',
    PALLET_CREATED: 'PALLET_CREATED',
    /** Új szint — raklapon a legfelső fölé, polcon a webről (G1). */
    LOCATION_LEVEL_ADDED: 'LOCATION_LEVEL_ADDED',
    /** EMBER törölte az üres LEGFELSŐ szintet (G1). */
    LOCATION_LEVEL_REMOVED: 'LOCATION_LEVEL_REMOVED',
    /** Polcszint lezárva / feloldva (G1) — csak a betárolást tiltja. */
    LOCATION_LEVEL_LOCKED: 'LOCATION_LEVEL_LOCKED',
    LOCATION_LEVEL_UNLOCKED: 'LOCATION_LEVEL_UNLOCKED',
    /**
     * A RENDSZER szüntette meg a kiürült raklapszintet, és a felette lévők
     * eggyel lejjebb csúsztak.
     *
     * A `sourceLevel` a megszűnt szint száma. SZÁNDÉKOSAN más érték, mint a
     * kézi `LOCATION_LEVEL_REMOVED`: az átszámozás megváltoztatja, hogy a
     * mappák ma hányadik szinten vannak, és ha a két ok egy értéken osztozna,
     * az előzményből nem lehetne megmondani, miért változott a szintszám.
     */
    PALLET_LEVEL_COLLAPSED: 'PALLET_LEVEL_COLLAPSED',
    PALLET_BULK_ASSIGNMENT_COMPLETED: 'PALLET_BULK_ASSIGNMENT_COMPLETED',
    PALLET_LEVEL_DISMANTLED: 'PALLET_LEVEL_DISMANTLED',
    PALLET_DISMANTLED: 'PALLET_DISMANTLED',
    PALLET_STATUS_CHANGED: 'PALLET_STATUS_CHANGED',
    LOCATION_CREATED: 'LOCATION_CREATED',
    LOCATION_STATUS_CHANGED: 'LOCATION_STATUS_CHANGED',
    /**
     * G2: a nyomtató törzsadattá vált — a PrintNode-azonosító a felületről
     * állítható, hogy egy nyomtatócsere ne legyen kitelepítés. A titok
     * (API-kulcs) Key Vaultban marad; csak a VÁLASZTÁS naplózódik.
     */
    PRINTER_CREATED: 'PRINTER_CREATED',
    PRINTER_UPDATED: 'PRINTER_UPDATED',
    /** B9 — címkekiosztás és -nyomtatás. Mindegyik `batchId`-t hordoz. */
    LABEL_BATCH_ALLOCATED: 'LABEL_BATCH_ALLOCATED',
    LABEL_BATCH_PRINT_SENT: 'LABEL_BATCH_PRINT_SENT',
    LABEL_BATCH_PRINT_FAILED: 'LABEL_BATCH_PRINT_FAILED',
    LABEL_BATCH_RESENT: 'LABEL_BATCH_RESENT',
    LABEL_BATCH_CONFIRMED: 'LABEL_BATCH_CONFIRMED',
    LABEL_BATCH_VOIDED: 'LABEL_BATCH_VOIDED',
    /**
     * G2: selejtezett sorszám visszaadása a forgalomba.
     *
     * Kockázatmentes, mert a selejtezés eleve CSAK olyan kiosztást érint,
     * amihez nem tartozik mappa (`voidBatch`) — egy visszaadott számra tehát
     * fogalmilag sosem történt regisztráció, így nincs két fizikai tárgy
     * ugyanazon az azonosítón.
     */
    LABEL_ALLOCATION_RESTORED: 'LABEL_ALLOCATION_RESTORED',
    /** Egyetlen sérült címke újranyomtatása — nincs köteg, `folderId`-t hordoz. */
    LABEL_REPRINTED: 'LABEL_REPRINTED',
};
export const EventResult = {
    SUCCESS: 'SUCCESS',
    FAILURE: 'FAILURE',
};
/**
 * Fizikai hozzáférhetőség a szint alapján (spec 13. § US-SEARCH-2).
 * Az 1. szint a legalsó, tehát a legnehezebben elérhető.
 */
export const Accessibility = {
    EASY: 'EASY',
    MEDIUM: 'MEDIUM',
    HARD: 'HARD',
};
/**
 * Besorolás; a legfelső szint a legkönnyebb.
 *
 * A második paraméter a D fázisban `maxLevel`-ről `topLevel`-re változott:
 * a raklapnak nincs többé előre rögzített maximuma, a szintjei dinamikusan
 * jönnek létre. A hozzáférhetőség így a MOST LÉTEZŐ legfelső szinthez
 * viszonyít — ami fizikailag pontosabb is, mert egy félig rakott raklap
 * legfelső szintje tényleg könnyen elérhető, akkor is, ha elférne fölé még
 * egy.
 */
export function accessibilityForLevel(levelNumber, topLevel) {
    if (levelNumber >= topLevel)
        return Accessibility.EASY;
    if (levelNumber === 1)
        return Accessibility.HARD;
    return Accessibility.MEDIUM;
}
/**
 * A besorolatlan cég kódja.
 *
 * A `folder.spine_company` oszlop DEFAULT-ja, ezért SOSEM törölhető és nem
 * deaktiválható — ezt a migráció CHECK constraintje kényszeríti ki
 * (`company_unclassified_always_active_ck`), nem konvenció.
 */
export const UNCLASSIFIED_COMPANY = 'BESOROLATLAN';
/**
 * A mappa tartalmának típusa a gerinckártya második sora szerint.
 *
 * Ez a mező nem szerepelt az eredeti kérésben, de MINDEN nyomtatott
 * kártyán ott van, és a leginkább szűrhető adat — ezért önálló mező.
 */
export const DocumentType = {
    BEVALLASOK: 'BEVALLASOK',
    PENZTAR: 'PENZTAR',
    LELTAR: 'LELTAR',
    FOKONYVI_KARTONOK: 'FOKONYVI_KARTONOK',
    BEERKEZETT_SZAMLAK: 'BEERKEZETT_SZAMLAK',
    KIMENO_SZAMLAK: 'KIMENO_SZAMLAK',
    TARGYI_ESZKOZ_KARTONOK: 'TARGYI_ESZKOZ_KARTONOK',
    OSREVIZIO: 'OSREVIZIO',
    FORRASOK: 'FORRASOK',
    EGYEB: 'EGYEB',
};
/** A gerincen szereplő pénznem (jellemzően a számlás mappáknál). */
export const SpineCurrency = {
    HUF: 'HUF',
    EUR: 'EUR',
};
//# sourceMappingURL=enums.js.map